/**
 * 订单号生成与解析。
 *
 * 要求（总指令第五部分第 3 条）：订单号必须唯一。
 * 设计：人类可读 + 可按天检索 + 带桌号便于厨房喊单 + 带随机位防猜测。
 *
 * 格式： M + yyyyMMdd + 4 位序号 + 3 位随机  = 例 M2026101300017
 *        ↑ 渠道前缀
 * 渠道前缀：M = 小程序扫码，K = 触屏机，C = 后台代点
 */
import { OrderChannel } from './types';

/** 订单号前缀与渠道的对应关系 */
export const ORDER_NO_PREFIX: Record<OrderChannel, string> = {
  MINI_PROGRAM: 'M',
  KIOSK: 'K',
  COUNTER: 'C',
};

const CHANNEL_BY_PREFIX: Record<string, OrderChannel> = {
  M: OrderChannel.MINI_PROGRAM,
  K: OrderChannel.KIOSK,
  C: OrderChannel.COUNTER,
};

/**
 * 取门店本地日期的 yyyyMMdd，用于订单号、取餐号、日报切天。
 *
 * 为什么不能用 `now.toISOString().slice(0,10)`：
 *   服务器时区可能是 UTC。北京时间 2026-10-13 早上 7 点，在 UTC 还是 10-12 的 23 点，
 *   直接切 ISO 日期会把早市的订单算到前一天，报表和序号全乱。
 *
 * @example storeDateKey(new Date('2026-10-12T16:30:00Z')) === '20261013'
 */
export function storeDateKey(now: Date = new Date(), timeZone = 'Asia/Shanghai'): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(now).replace(/-/g, '');
}

/** 生成订单号时用的时间部件（按门店本地时区，默认北京时间） */
function datePart(now: Date, timeZone = 'Asia/Shanghai'): string {
  // 用 Intl 取门店当地日期，避免服务器在 UTC 时跨天算错（中国门店 08:00 是 UTC 00:00）
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(now).replace(/-/g, '');
}

/**
 * 生成订单号。
 * @param channel 订单渠道，决定前缀
 * @param dailySeq 当天自增序号（调用方从数据库计数器取，保证不重复）
 * @param now 当前时间
 * @param timeZone 门店时区
 */
export function buildOrderNo(
  channel: OrderChannel,
  dailySeq: number,
  now: Date = new Date(),
  timeZone = 'Asia/Shanghai',
): string {
  const prefix = ORDER_NO_PREFIX[channel];
  if (!prefix) {
    throw new Error(`未知订单渠道：${String(channel)}`);
  }
  if (!Number.isSafeInteger(dailySeq) || dailySeq < 1 || dailySeq > 9999) {
    throw new Error(`订单序号必须是 1..9999 的整数（当前值：${String(dailySeq)}）`);
  }
  // 3 位随机，降低「猜订单号看别人订单」的风险；真正的权限校验不依赖它
  const rand = Math.floor(Math.random() * 1000);
  return `${prefix}${datePart(now, timeZone)}${String(dailySeq).padStart(4, '0')}${String(rand).padStart(3, '0')}`;
}

export interface ParsedOrderNo {
  channel: OrderChannel;
  /** yyyyMMdd */
  date: string;
  dailySeq: number;
}

/** 解析订单号，解析失败返回 null（不抛错，便于当校验函数用） */
export function parseOrderNo(orderNo: string): ParsedOrderNo | null {
  const matched = /^([MKC])(\d{8})(\d{4})(\d{3})$/.exec(orderNo);
  if (!matched) return null;
  const channel = CHANNEL_BY_PREFIX[matched[1]];
  if (!channel) return null;
  return {
    channel,
    date: matched[2],
    dailySeq: Number(matched[3]),
  };
}

/** 校验订单号格式是否合法 */
export function isValidOrderNo(orderNo: string): boolean {
  return parseOrderNo(orderNo) !== null;
}

/**
 * 取款/取餐号（叫号用）：当天序号本身，厨房看这个比看长订单号快。
 */
export function buildPickupNo(dailySeq: number): string {
  if (!Number.isSafeInteger(dailySeq) || dailySeq < 1) {
    throw new Error(`取餐号必须是正整数（当前值：${String(dailySeq)}）`);
  }
  return String(dailySeq).padStart(3, '0');
}
